import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { isoToBritishInput, britishInputToIso, formatBritishTime } from '@/lib/date';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Loader2, Save } from 'lucide-react';
import OrgSpeciesSearch, { type Species } from '@/components/OrgSpeciesSearch';
import { updateRecordSchema } from "@tyto/shared"
import { PageTopNav } from '@/components/nav/PageTopNav';

// Define the Record type
interface Record {
  id: string;
  speciesId: string;
  englishName: string;
  scientificName: string;
  dtm: string;
  count: number;
  lat: number | null;
  lng: number | null;
  note: string;
  locationNote: string;
  createdAt: string;
  updatedAt: string;
}


// Form type
type RecordFormValues = z.infer<typeof updateRecordSchema>;

interface EditRecordFormProps {
  recordId: string;
  orgId: string;
  record: Record;
  onSaveComplete: () => void;
  onCancel: () => void;
}

const EditRecordForm: React.FC<EditRecordFormProps> = ({
  recordId,
  orgId,
  record,
  onSaveComplete,
  onCancel
}) => {
  const queryClient = useQueryClient();
  const [selectedSpecies, setSelectedSpecies] = useState<Species | null>(null);

  // Setup form with default values from the record
  const form = useForm<RecordFormValues>({
    resolver: zodResolver(updateRecordSchema),
    defaultValues: {
      speciesId: record.speciesId,
      siteId: null,
      note: record.note,
      locationNote: record.locationNote,
      dtm: record.dtm,
      lat: record.lat ?? null,
      lng: record.lng ?? null,
      count: record.count ?? null,
    }
  });

  // Update mutation for the record
  const { mutate: updateRecord, isPending, error: updateError } = useMutation({
    mutationFn: async (data: RecordFormValues) => {
      const response = await fetch(`/api/record/${recordId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Failed to update record:', errorData);
        throw new Error(errorData.error || 'Failed to update record');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['record', recordId] });
      queryClient.invalidateQueries({ queryKey: ['organizationRecords'] });
      onSaveComplete();
    },
  });

  // Form submission handler
  const onSubmit = (data: RecordFormValues) => {
    // Convert string values to numbers where needed
    const formattedData = {
      ...data,
      lat: data.lat !== undefined ? Number(data.lat) : null,
      lng: data.lng !== undefined ? Number(data.lng) : null,
      count: data.count !== undefined ? Number(data.count) : null,
    };

    updateRecord(formattedData);
  };

  // Set selected species based on record data
  useEffect(() => {
    if (record) {
      setSelectedSpecies({
        id: record.speciesId,
        speciesId: record.speciesId,
        status: '',
        note: '',
        order: '',
        scientificName: record.scientificName,
        englishName: record.englishName,
      });
    }
  }, [record]);

  useEffect(() => {
    if (selectedSpecies) {
      form.setValue('speciesId', selectedSpecies.speciesId);
    }
  }, [selectedSpecies]);

  return (
    <div className="space-y-6">
      <PageTopNav pageName="Edit Record" rightItem={<Button variant="ghost" onClick={onCancel}>Cancel</Button>} />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <FormField
            control={form.control}
            name="speciesId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Species</FormLabel>
                <FormControl>
                  <OrgSpeciesSearch
                    orgId={orgId}
                    onSelect={(species) => {
                      if (species) {
                        field.onChange(species.id);
                        setSelectedSpecies(species);
                      }
                    }}
                    selectedSpecies={selectedSpecies}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="dtm"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Date & Time</FormLabel>
                <FormControl>
                  <Input
                    type="datetime-local"
                    value={isoToBritishInput(field.value)}
                    onChange={x => field.onChange(britishInputToIso(x.target.value))}
                  />
                </FormControl>
                <div className="text-xs text-muted-foreground mt-1">
                  {field.value && formatBritishTime(field.value)}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="count"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Count</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      {...field}
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="lat"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Latitude</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.000001"
                      {...field}
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lng"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Longitude</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.000001"
                      {...field}
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="locationNote"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Location Notes</FormLabel>
                <FormControl>
                  <Textarea {...field} value={field.value || ''} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="note"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Observation Notes</FormLabel>
                <FormControl>
                  <Textarea {...field} value={field.value || ''} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {Object.keys(form.formState.errors).length > 0 && (
            <Alert variant="destructive">
              <AlertTitle>Form Validation Error</AlertTitle>
              <AlertDescription>
                {Object.entries(form.formState.errors).map(([field, error]) => (
                  <div key={field} className="mt-1">
                    <strong>{field}:</strong> {error?.message}
                  </div>
                ))}
              </AlertDescription>
            </Alert>
          )}

          {updateError && (
            <Alert variant="destructive">
              <AlertTitle>An Error Occurred</AlertTitle>
              <AlertDescription>
                {JSON.stringify(updateError.message)}
              </AlertDescription>
            </Alert>
          )}

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default EditRecordForm;
